/**
 * Primary Filter Component for IRP Workbook
 *
 * Date range + source chips + descriptor filter that updates the primary filter in the store,
 * which invalidates BFF caches and triggers re-fetches.
 */

import React, {useEffect, useMemo, useState} from 'react';
import {Autocomplete, Button, Chip, Stack, TextField, Typography} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import {
    useAvailableDescriptors, useAvailableSources, useIRPWorkbookStore, useIRPWorkbookStoreApi, usePrimaryFilter
} from '../../../../../../stores/irp-workbook.store';
import {DescriptorTerm} from '../../../../../../@types/descriptor-term';
import {useDebounce} from '../../../../../../hooks/useDebounce';

/**
 * Convert a store Date (UTC midnight) to a Dayjs value for the DatePicker.
 * UTC methods are used when extracting the date string to avoid local-timezone
 * shifting (e.g. a UTC midnight date appearing as the previous day in UTC-5).
 */
const dateToDayjs = (date: Date | null): Dayjs | null => {
    if (!date) return null;
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return dayjs(`${year}-${month}-${day}`);
};

/**
 * Primary Filter Component - Date, Source, and Descriptor Filtering
 */
export const PrimaryFilter: React.FC = () => {
    const primaryFilter = usePrimaryFilter();
    const setPrimaryFilter = useIRPWorkbookStore((state) => state.setPrimaryFilter);
    const availableDescriptors = useAvailableDescriptors();
    const availableSources = useAvailableSources();
    const storeApi = useIRPWorkbookStoreApi();

    // Local Dayjs state mirrors what is shown in the pickers.
    // Store commits happen only via onAccept (calendar click or Enter),
    // so arrow-key navigation never triggers an API call.
    const [localDateFrom, setLocalDateFrom] = useState<Dayjs | null>(() => dateToDayjs(primaryFilter.dateFrom));
    const [localDateTo, setLocalDateTo] = useState<Dayjs | null>(() => dateToDayjs(primaryFilter.dateTo));

    // Local source selection — toggled immediately for UI feedback, debounced before committing to store.
    // Empty set means all sources are included (same convention as the store).
    const [localSelectedSources, setLocalSelectedSources] = useState<Set<string>>(
        () => new Set(primaryFilter.selectedSources)
    );

    // Sync local state when primaryFilter dates are changed externally
    // (e.g., Clear Filters, IRP switch).
    useEffect(() => {
        setLocalDateFrom(dateToDayjs(primaryFilter.dateFrom));
    }, [primaryFilter.dateFrom]);

    useEffect(() => {
        setLocalDateTo(dateToDayjs(primaryFilter.dateTo));
    }, [primaryFilter.dateTo]);

    // Sync local sources when the store changes externally (Clear Filters, IRP switch).
    useEffect(() => {
        setLocalSelectedSources(new Set(primaryFilter.selectedSources));
    }, [primaryFilter.selectedSources]);

    // Debounce source changes: serialize to a stable string, wait 400ms, then commit.
    const debouncedSourcesKey = useDebounce(
        Array.from(localSelectedSources).sort().join('\0'),
        400
    );

    useEffect(() => {
        const storeKey = Array.from(storeApi.getState().primaryFilter.selectedSources).sort().join('\0');
        if (debouncedSourcesKey === storeKey) return;
        const newSources = debouncedSourcesKey === ''
            ? new Set<string>()
            : new Set(debouncedSourcesKey.split('\0'));
        setPrimaryFilter({ ...storeApi.getState().primaryFilter, selectedSources: newSources });
    }, [debouncedSourcesKey]); // storeApi and setPrimaryFilter are stable references — intentionally omitted

    // onAccept fires only when the user explicitly confirms a date —
    // calendar day click, Enter key, or picker "OK" button.
    const handleDateFromAccept = (value: Dayjs | null): void => {
        const newFrom = value ? new Date(value.format('YYYY-MM-DD') + 'T00:00:00Z') : null;
        const currentFilter = storeApi.getState().primaryFilter;
        setPrimaryFilter({ ...currentFilter, dateFrom: newFrom });
    };

    const handleDateToAccept = (value: Dayjs | null): void => {
        const newTo = value ? new Date(value.format('YYYY-MM-DD') + 'T23:59:59.999Z') : null;
        const currentFilter = storeApi.getState().primaryFilter;
        setPrimaryFilter({ ...currentFilter, dateTo: newTo });
    };

    // Toggle a source chip. At least one source must remain selected.
    const handleSourceToggle = (source: string): void => {
        setLocalSelectedSources((prev) => {
            const allSelected = prev.size === 0;

            if (allSelected) {
                // Uncheck one source — select all others
                const next = new Set(availableSources.filter((s) => s !== source));
                if (next.size === 0) return prev; // last chip — cannot uncheck
                return next;
            }

            if (prev.has(source)) {
                if (prev.size === 1) return prev; // last checked chip — cannot uncheck
                const next = new Set(prev);
                next.delete(source);
                // Normalize: if all sources are now checked, use empty set
                if (next.size === availableSources.length) return new Set<string>();
                return next;
            }

            // Check an unchecked source
            const next = new Set(prev);
            next.add(source);
            if (next.size === availableSources.length) return new Set<string>();
            return next;
        });
    };

    // Handle descriptor selection change — no debounce needed (discrete selection).
    const handleDescriptorChange = (_event: React.SyntheticEvent, selected: DescriptorTerm[]): void => {
        setPrimaryFilter({
            ...primaryFilter,
            selectedDescriptors: new Set(selected.map((d) => d.id))
        });
    };

    // Clear all filters.
    const handleClearFilters = (): void => {
        setLocalDateFrom(null);
        setLocalDateTo(null);
        setLocalSelectedSources(new Set<string>());
        setPrimaryFilter({
            dateFrom: null,
            dateTo: null,
            selectedDescriptors: new Set<number>(),
            selectedSources: new Set<string>(),
        });
    };

    // Derive selected DescriptorTerm objects from the Set of IDs in primaryFilter
    const selectedTerms = useMemo((): DescriptorTerm[] => {
        if (primaryFilter.selectedDescriptors.size === 0) return [];
        return availableDescriptors.filter((d) => primaryFilter.selectedDescriptors.has(d.id));
    }, [availableDescriptors, primaryFilter.selectedDescriptors]);

    const showClearButton = useMemo((): boolean => {
        return !!(localDateFrom || localDateTo || primaryFilter.selectedDescriptors.size > 0 || localSelectedSources.size > 0);
    }, [localDateFrom, localDateTo, primaryFilter.selectedDescriptors, localSelectedSources]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2} sx={{ width: '100%' }}>
                {/* Date range */}
                <Stack direction="row" spacing={1}>
                    <DatePicker
                        label="From Date (UTC)"
                        value={localDateFrom}
                        onChange={setLocalDateFrom}
                        onAccept={handleDateFromAccept}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                    <DatePicker
                        label="To Date (UTC)"
                        value={localDateTo}
                        onChange={setLocalDateTo}
                        onAccept={handleDateToAccept}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                </Stack>

                {/* Source Chips — always shown when sources are known */}
                {availableSources.length > 0 && (
                    <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">Sources</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {availableSources.map((source) => {
                                const checked = localSelectedSources.size === 0 || localSelectedSources.has(source);
                                const isLastChecked =
                                    (localSelectedSources.size === 0 && availableSources.length === 1) ||
                                    (localSelectedSources.size === 1 && localSelectedSources.has(source));
                                const disabled = checked && isLastChecked;
                                return (
                                    <Chip
                                        key={source}
                                        label={source}
                                        size="small"
                                        color={checked ? 'primary' : 'default'}
                                        variant={checked ? 'filled' : 'outlined'}
                                        onClick={disabled ? undefined : (): void => handleSourceToggle(source)}
                                        sx={{ cursor: disabled ? 'default' : 'pointer' }}
                                    />
                                );
                            })}
                        </Stack>
                    </Stack>
                )}

                {/* Descriptor Term Filter */}
                <Autocomplete
                    multiple
                    size="small"
                    options={availableDescriptors}
                    value={selectedTerms}
                    onChange={handleDescriptorChange}
                    groupBy={(option): string => option.descriptorName}
                    getOptionLabel={(option): string => option.term}
                    isOptionEqualToValue={(option, value): boolean => option.id === value.id}
                    disableCloseOnSelect
                    sx={{ width: '100%' }}
                    renderInput={(params): React.ReactElement => (
                        <TextField
                            {...params}
                            label="Descriptor Terms"
                            placeholder={selectedTerms.length === 0 ? 'Filter by descriptor...' : ''}
                        />
                    )}
                    renderTags={(value, getTagProps): React.ReactElement[] =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                                <Chip
                                    key={key}
                                    label={option.term}
                                    size="small"
                                    {...tagProps}
                                />
                            );
                        })
                    }
                />

                {/* Reset Filters Button */}
                {showClearButton && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearFilters}
                        sx={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}
                    >
                        Clear Filters
                    </Button>
                )}
            </Stack>
        </LocalizationProvider>
    );
};

export default PrimaryFilter;
